import { gql } from 'graphql-request';
import { PortfolioSiteMetric } from 'src/shared/types/graph/portfolio';

const siteTableRowMetricsFragment = `
    metrics {
        name
        label
        frameworks
        ... on MultiMetricSingleYearSiteMetric {
            year
            metrics
        }
        ... on SingleYearSiteMetric {
            year
            data
        }
        ... on MultiYearSiteMetric {
            years
            datum
        }
        ... on MultiYearMetricSingleYearSiteMetric {
            year
            years
            metricWithTrend
        }
    }
`;

export const portfolioMapFiltersQuery = gql`
    query getPortfolioMapFilters($customerId: String!) {
        portfolio {
            getPortfolioFilters(customerId: $customerId) {
                availableMetrics
            }
        }
    }
`;

export type PortfolioMapFiltersQueryResult = {
    portfolio: {
        getPortfolioFilters: {
            availableMetrics: string[];
        };
    };
};

const MAP_MARKER_FIELDS = `
    markers {
        siteId
        name
        coordinates
        metricValue
        speciesRedListCount
        speciesInvasiveCount
        hasEdna
        hasHabitatInsights
    }
    totalInBounds
    totalOverall
`;

export const mapMarkersQuery = gql`
    query getMapMarkers(
        $customerId: String!
        $metric: HlcaMetricType
        $bounds: BoundsInput
        $filters: [CustomFilterOptionInput]
        $pagination: PaginationInput
        $sortBy: SortByInput
    ) {
        portfolio {
            getMapMarkers(
                customerId: $customerId
                metric: $metric
                bounds: $bounds
                filters: $filters
                pagination: $pagination
                sortBy: $sortBy
            ) {
                ${MAP_MARKER_FIELDS}
            }
        }
    }
`;

export type MapMarkersQueryResult = {
    portfolio: {
        getMapMarkers: MapMarkersResponse;
    };
};

export type MapMarker = {
    siteId: string;
    name: string;
    coordinates: number[];
    metricValue: number | null;
    speciesRedListCount: number | null;
    speciesInvasiveCount: number | null;
    hasEdna: boolean;
    hasHabitatInsights: boolean;
};

export type MapMarkersResponse = {
    markers: MapMarker[];
    totalInBounds: number;
    totalOverall: number;
};

const SITE_TABLE_FIELDS = `
    rows {
        id
        name
        area
        country
        metricValue
        ${siteTableRowMetricsFragment}
        speciesRedListCount
        speciesInvasiveCount
        hasEdna
        hasHabitatInsights
    }
    pagination {
        page
        pageSize
        total
    }
`;

export const siteTableQuery = gql`
    query getSiteTable(
        $customerId: String!
        $metric: HlcaMetricType
        $filters: [CustomFilterOptionInput]
        $pagination: PaginationInput!
        $sortBy: SortByInput
        $searchText: String
    ) {
        portfolio {
            getSiteTable(
                customerId: $customerId
                metric: $metric
                filters: $filters
                pagination: $pagination
                sortBy: $sortBy
                searchText: $searchText
            ) {
                ${SITE_TABLE_FIELDS}
            }
        }
    }
`;

export type SiteTableQueryResult = {
    portfolio: {
        getSiteTable: SiteTableResponse;
    };
};

export type SiteTableRow = {
    id: string;
    name: string;
    area: number;
    country: string;
    metricValue: number | null;
    metrics: PortfolioSiteMetric[];
    speciesRedListCount: number | null;
    speciesInvasiveCount: number | null;
    hasEdna: boolean;
    hasHabitatInsights: boolean;
};

export type SiteTableResponse = {
    rows: SiteTableRow[];
    pagination: {
        page: number;
        pageSize: number;
        total: number;
    };
};

const SITE_POPUP_FIELDS = `
    id
    name
    totalArea
    country
    latestYear
    metrics {
        name
        label
        frameworks
        ... on MultiMetricSingleYearSiteMetric {
            year
            metrics
        }
        ... on SingleYearSiteMetric {
            year
            data
        }
        ... on MultiYearSiteMetric {
            years
            datum
        }
        ... on MultiYearMetricSingleYearSiteMetric {
            year
            years
            metricWithTrend
        }
    }
    speciesRedListCount
    speciesInvasiveCount
    speciesTotalCount
`;

export const sitePopupQuery = gql`
    query getSitePopup($customerId: String!, $siteId: String!) {
        portfolio {
            getSitePopup(customerId: $customerId, siteId: $siteId) {
                ${SITE_POPUP_FIELDS}
            }
        }
    }
`;

export type SitePopupQueryResult = {
    portfolio: {
        getSitePopup: SitePopupData;
    };
};

export type SitePopupData = {
    id: string;
    name: string;
    totalArea: number;
    country: string;
    latestYear: number | null;
    metrics: PortfolioSiteMetric[];
    speciesRedListCount: number;
    speciesInvasiveCount: number;
    speciesTotalCount: number;
};

export const sitePolygonsQuery = gql`
    query getSitePolygons($customerId: String!, $siteId: String!) {
        portfolio {
            getSitePolygons(customerId: $customerId, siteId: $siteId) {
                name
                coordinates
            }
        }
    }
`;

export type SitePolygonData = {
    name: string;
    coordinates: number[][][][];
};

export type SitePolygonsQueryResult = {
    portfolio: {
        getSitePolygons: SitePolygonData[];
    };
};

const SITE_SPECIES_ASSESSMENT_FIELDS = `
    scientificName
    commonName
    iucnRedListCategory
    predictionLevel
    hasEDNAData
    hasUploadedData
    foundInEDNA
    foundInUploadedData
    isNew
`;

// Dedicated site species query for the site species risk modal
export const siteSpeciesRiskQuery = gql`
    query getSiteSpeciesRisk($siteId: String!, $filters: [CustomFilterOptionInput]) {
        siteSummary {
            getSiteSpeciesRisk(siteId: $siteId, filters: $filters) {
                snapshot
                siteName
                country
                area
                redListSpecies {
                    ${SITE_SPECIES_ASSESSMENT_FIELDS}
                }
                invasiveSpecies {
                    ${SITE_SPECIES_ASSESSMENT_FIELDS}
                }
            }
        }
    }
`;

export type SiteSpeciesAssessmentItem = {
    scientificName: string;
    commonName?: string | null;
    iucnRedListCategory: string;
    predictionLevel: string;
    hasEDNAData: boolean;
    hasUploadedData: boolean;
    foundInEDNA: boolean;
    foundInUploadedData: boolean;
    /** First appeared at this site in the served snapshot (DD-6012). */
    isNew?: boolean;
};

export type SiteSpeciesRiskData = {
    snapshot: string;
    siteName: string;
    country: string;
    area: number;
    redListSpecies: SiteSpeciesAssessmentItem[];
    invasiveSpecies: SiteSpeciesAssessmentItem[];
};

export type SiteSpeciesRiskQueryResult = {
    siteSummary: {
        getSiteSpeciesRisk: SiteSpeciesRiskData;
    };
};
